<div class="schedule-editor-modal">
  <!-- Modern Header -->
  <div class="editor-header">
    <div class="header-content">
      <div class="header-icon" [class.clinic]="isClinic">
        <i class="fa {{ isClinic ? 'fa-calendar-day' : 'fa-list-ol' }}"></i>
      </div>
      <div class="header-text">
        <h4 class="header-title">{{ schedule.schedule_name }}</h4>
        <span class="header-subtitle">{{ eventClass.class_name }}</span>
      </div>
    </div>
    <button type="button" class="close-btn" aria-label="Close" (click)="close()">
      <i class="fa fa-times"></i>
    </button>
  </div>

  <!-- Body -->
  <div class="editor-body" #scrollContainer (dragover)="onContainerDragOver($event)">
    @if (loading) {
      <div class="loading-state">
        <div class="spinner"></div>
        <p>{{ 'scheduleEditor.loading' | translate }}</p>
      </div>
    } @else if (isClinic) {
      <!-- CLINIC DAY SCHEDULE VIEW -->
      <div class="clinic-layout">
        <!-- Left Sidebar -->
        <div class="sidebar">
          <!-- Settings Panel -->
          <div class="panel">
            <div class="panel-header">
              <i class="fa fa-cog"></i>
              <span>{{ 'scheduleEditor.settings' | translate }}</span>
            </div>
            <div class="panel-body">
              <div class="form-group">
                <label>{{ 'scheduleEditor.scheduleName' | translate }}</label>
                <input type="text" class="modern-input" [(ngModel)]="scheduleName" (ngModelChange)="onClinicSettingsChange()" [placeholder]="'scheduleEditor.enterName' | translate" />
              </div>
              <div class="form-group">
                <label>{{ 'scheduleEditor.startDateTime' | translate }}</label>
                <input type="datetime-local" class="modern-input" [(ngModel)]="scheduleStartDateTime" (ngModelChange)="onScheduleStartDateTimeChange($event)" />
              </div>
            </div>
          </div>

          <!-- Unassigned Participants Panel -->
          <div class="panel participants-panel">
            <div class="panel-header">
              <i class="fa fa-user-plus"></i>
              <span>{{ 'scheduleEditor.unassigned' | translate }}</span>
              <span class="badge">{{ unassignedParticipants.length }}</span>
            </div>
            <div class="panel-body participant-list">
              @if (unassignedParticipants.length === 0) {
                <div class="empty-state small">
                  <i class="fa fa-check-circle"></i>
                  <p>{{ 'scheduleEditor.allAssigned' | translate }}</p>
                </div>
              } @else {
                @for (entry of unassignedParticipants; track entry.entry_id) {
                  <div
                    class="participant-card"
                    draggable="true"
                    (dragstart)="onEntryDragStart($event, entry, null)"
                    (dragend)="onDragEnd()"
                  >
                    <div class="drag-indicator">
                      <i class="fa fa-grip-vertical"></i>
                    </div>
                    <div class="participant-details">
                      <span class="name">{{ getParticipantName(entry) }}</span>
                      @if (getHorseName(entry)) {
                        <span class="horse">{{ getHorseName(entry) }}</span>
                      }
                    </div>
                  </div>
                }
              }
            </div>
          </div>

          <!-- All Participants Panel -->
          <div class="panel participants-panel">
            <div class="panel-header">
              <i class="fa fa-users"></i>
              <span>{{ 'scheduleEditor.allParticipants' | translate }}</span>
              <span class="badge">{{ allParticipants.length }}</span>
            </div>
            <div class="panel-body participant-list">
              @if (allParticipants.length === 0) {
                <div class="empty-state small">
                  <i class="fa fa-user-slash"></i>
                  <p>{{ 'scheduleEditor.noParticipants' | translate }}</p>
                </div>
              } @else {
                @for (entry of allParticipants; track entry.entry_id) {
                  <div
                    class="participant-card"
                    [class.assigned]="getGroupCount(entry) > 0"
                    draggable="true"
                    (dragstart)="onEntryDragStart($event, entry, -1)"
                    (dragend)="onDragEnd()"
                  >
                    <div class="drag-indicator">
                      <i class="fa fa-grip-vertical"></i>
                    </div>
                    <div class="participant-details">
                      <span class="name">{{ getParticipantName(entry) }}</span>
                      @if (getHorseName(entry)) {
                        <span class="horse">{{ getHorseName(entry) }}</span>
                      }
                      @if (getGroupCount(entry) > 0) {
                        <div class="assigned-groups">
                          @for (group of getParticipantGroups(entry); track group.local_id) {
                            <span class="group-tag">{{ group.name }}</span>
                          }
                        </div>
                      }
                    </div>
                  </div>
                }
              }
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">
          <!-- Toolbar -->
          <div class="toolbar">
            <div class="toolbar-title">
              <i class="fa fa-stream"></i>
              <h5>{{ 'scheduleEditor.daySchedule' | translate }}</h5>
            </div>
            <div class="toolbar-actions">
              @if (hasChanges) {
                <button class="btn-modern primary" [disabled]="saving" (click)="saveClinicSchedule()">
                  <i class="fa {{ saving ? 'fa-spinner fa-spin' : 'fa-check' }}"></i>
                  {{ saving ? ('scheduleEditor.saving' | translate) : ('scheduleEditor.saveChanges' | translate) }}
                </button>
              }
            </div>
          </div>

          <!-- Timeline -->
          @if (groups.length === 0) {
            <div class="empty-state centered">
              <div class="empty-icon">
                <i class="fa fa-layer-group"></i>
              </div>
              <h6>{{ 'scheduleEditor.noGroups' | translate }}</h6>
              <p>{{ 'scheduleEditor.createGroupsHint' | translate }}</p>
              <button class="btn-modern outline" (click)="addGroup()">
                <i class="fa fa-plus"></i>
                {{ 'scheduleEditor.addGroup' | translate }}
              </button>
            </div>
          } @else {
            <div class="timeline">
              @for (block of scheduleBlocks; track $index; let i = $index) {
                @if (block.type === 'break') {
                  <!-- Break Indicator -->
                  <div class="break-indicator">
                    <div class="break-line"></div>
                    <div class="break-pill">
                      <i class="fa fa-coffee"></i>
                      <span>{{ block.break_notes }} ({{ block.break_duration_minutes }} min)</span>
                      <button class="remove-break" (click)="removeBreakBetweenGroups(block.previousGroupLocalId!)" title="Remove break">
                        <i class="fa fa-times"></i>
                      </button>
                    </div>
                    <div class="break-line"></div>
                  </div>
                } @else if (block.type === 'group' && block.group) {
                  <!-- Group Card -->
                  <div
                    class="group-card"
                    [class.drag-target]="draggedEntry && draggedFromGroup !== block.group.local_id"
                    (dragover)="onGroupDragOver($event)"
                    (drop)="onGroupDrop($event, block.group.local_id)"
                  >
                    <!-- Time Badge -->
                    <div class="time-badge">
                      <input
                        type="time"
                        class="time-input"
                        [ngModel]="block.group.start_time"
                        (ngModelChange)="updateGroupStartTime(block.group, $event)"
                      />
                      <span class="time-divider">{{ 'scheduleEditor.timeTo' | translate }}</span>
                      <span class="end-time">{{ getGroupEndTime(block.group) }}</span>
                    </div>

                    <!-- Group Header -->
                    <div class="group-card-header">
                      <input
                        type="text"
                        class="group-name-input"
                        [ngModel]="block.group.name"
                        (ngModelChange)="updateGroupName(block.group, $event)"
                        [placeholder]="'scheduleEditor.groupNamePlaceholder' | translate"
                      />
                      <select
                        class="duration-select"
                        [ngModel]="block.group.duration_minutes"
                        (ngModelChange)="updateGroupDuration(block.group, $event)"
                      >
                        @for (preset of durationPresets; track preset.value) {
                          <option [ngValue]="preset.value">{{ preset.label }}</option>
                        }
                      </select>
                      <div class="group-menu">
                        <button class="menu-trigger" data-bs-toggle="dropdown">
                          <i class="fa fa-ellipsis-h"></i>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end">
                          <li class="dropdown-header">{{ 'scheduleEditor.addBreakAfter' | translate }}</li>
                          <li>
                            <button class="dropdown-item" (click)="addBreakAfterGroup(block.group, 15)">
                              <i class="fa fa-clock"></i> {{ 'scheduleEditor.minutes15' | translate }}
                            </button>
                          </li>
                          <li>
                            <button class="dropdown-item" (click)="addBreakAfterGroup(block.group, 30)">
                              <i class="fa fa-clock"></i> {{ 'scheduleEditor.minutes30' | translate }}
                            </button>
                          </li>
                          <li>
                            <button class="dropdown-item" (click)="addBreakAfterGroup(block.group, 60)">
                              <i class="fa fa-coffee"></i> {{ 'scheduleEditor.hour1Lunch' | translate }}
                            </button>
                          </li>
                          <li><hr class="dropdown-divider"></li>
                          <li>
                            <button class="dropdown-item danger" (click)="removeGroup(block.group)">
                              <i class="fa fa-trash"></i> {{ 'scheduleEditor.deleteGroup' | translate }}
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <!-- Participants Area -->
                    <div class="group-card-body">
                      @if (block.group.entries.length === 0) {
                        <div class="drop-zone">
                          <i class="fa fa-hand-point-left"></i>
                          <span>{{ 'scheduleEditor.dragParticipants' | translate }}</span>
                        </div>
                      } @else {
                        <div class="group-participants">
                          @for (entry of block.group.entries; track entry.entry_id) {
                            <div
                              class="participant-entry"
                              draggable="true"
                              (dragstart)="onEntryDragStart($event, entry, block.group.local_id)"
                              (dragend)="onDragEnd()"
                            >
                              <i class="fa fa-grip-vertical drag-handle"></i>
                              <span class="entry-name">{{ getParticipantName(entry) }}</span>
                              @if (getHorseName(entry)) {
                                <span class="entry-horse">{{ getHorseName(entry) }}</span>
                              }
                              <button
                                class="remove-entry"
                                (click)="removeFromGroup(entry, block.group.local_id); $event.stopPropagation()"
                                title="Remove"
                              >
                                <i class="fa fa-times"></i>
                              </button>
                            </div>
                          }
                        </div>
                      }
                    </div>

                    <!-- Footer -->
                    <div class="group-card-footer">
                      <span class="count">
                        <i class="fa fa-user"></i>
                        {{ block.group.entries.length }} {{ block.group.entries.length !== 1 ? ('scheduleEditor.participants' | translate) : ('scheduleEditor.participant' | translate) }}
                      </span>
                    </div>
                  </div>
                }
              }
            </div>

            <!-- Add Group Button (centered below timeline) -->
            <div class="add-group-container">
              <button class="btn-modern outline" (click)="addGroup()">
                <i class="fa fa-plus"></i>
                {{ 'scheduleEditor.addGroup' | translate }}
              </button>
            </div>
          }
        </div>
      </div>
    } @else {
      <!-- STANDARD START LIST VIEW -->
      <div class="standard-layout">
        <div class="sidebar">
          <div class="panel">
            <div class="panel-header">
              <i class="fa fa-cog"></i>
              <span>{{ 'scheduleEditor.settings' | translate }}</span>
            </div>
            <div class="panel-body">
              <div class="form-group">
                <label>{{ 'scheduleEditor.scheduleName' | translate }}</label>
                <input type="text" class="modern-input" [(ngModel)]="scheduleName" (ngModelChange)="onStartListSettingsChange()" />
              </div>
              <div class="form-group">
                <label>{{ 'scheduleEditor.startDateTime' | translate }}</label>
                <input type="datetime-local" class="modern-input" [(ngModel)]="scheduleStartDateTime" (ngModelChange)="onStartListDateTimeChange($event)" />
              </div>
              <div class="form-group">
                <label>{{ 'scheduleEditor.timePerEntry' | translate }} ({{ 'scheduleEditor.minutesUnit' | translate }})</label>
                <input
                  type="number"
                  class="modern-input"
                  min="0.5"
                  step="0.5"
                  [ngModel]="timePerEntryMinutes"
                  (ngModelChange)="updateTimePerEntryMinutes($event)"
                />
              </div>
              <div class="form-group">
                <label>{{ 'scheduleEditor.advancementMode' | translate }}</label>
                <select class="modern-input" [(ngModel)]="advancementMode" (ngModelChange)="onStartListSettingsChange()">
                  <option value="manual">{{ 'scheduleEditor.modeManual' | translate }}</option>
                  <option value="automatic">{{ 'scheduleEditor.modeAutomatic' | translate }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Save Button -->
          <button class="btn-modern primary full-width mt-3" [disabled]="saving" (click)="saveStartList()">
            <i class="fa {{ saving ? 'fa-spinner fa-spin' : 'fa-save' }}"></i>
            {{ saving ? ('scheduleEditor.saving' | translate) : ('scheduleEditor.save' | translate) }}
          </button>
        </div>

        <div class="main-content">
          <!-- Toolbar -->
          <div class="toolbar">
            <div class="toolbar-title">
              <i class="fa fa-list-ol"></i>
              <h5>{{ 'scheduleEditor.startList' | translate }}</h5>
              <span class="entry-count">{{ getActiveParticipantCount() }} {{ 'scheduleEditor.entries' | translate }}</span>
            </div>
            <div class="toolbar-actions">
              @if (hasChanges) {
                <button class="btn-modern primary" [disabled]="saving" (click)="saveStartList()">
                  <i class="fa {{ saving ? 'fa-spinner fa-spin' : 'fa-check' }}"></i>
                  {{ saving ? ('scheduleEditor.saving' | translate) : ('scheduleEditor.saveChanges' | translate) }}
                </button>
              }
            </div>
          </div>

          <!-- Start List -->
          <div class="start-list-container">
            @if (entries.length === 0) {
              <div class="empty-state centered">
                <div class="empty-icon">
                  <i class="fa fa-list-ol"></i>
                </div>
                <h6>{{ 'scheduleEditor.noEntries' | translate }}</h6>
                <p>{{ 'scheduleEditor.entriesHint' | translate }}</p>
              </div>
            } @else {
              <div class="start-list-timeline">
                @for (entry of entries; track entry.entry_id; let i = $index) {
                  @if (entry.entry_type === 'break') {
                    <!-- Break Entry -->
                    <div class="start-list-break">
                      <div class="break-time">
                        {{ getEntryEstimatedTime(entry, i) }}
                      </div>
                      <div class="break-content">
                        <div class="break-pill">
                          <i class="fa fa-wrench"></i>
                          <input
                            type="text"
                            class="break-notes-input"
                            [ngModel]="entry.notes"
                            (ngModelChange)="updateBreakNotes(entry, $event)"
                            [placeholder]="'scheduleEditor.breakNotesPlaceholder' | translate"
                          />
                          <input
                            type="number"
                            class="break-duration-select"
                            min="1"
                            step="1"
                            [ngModel]="(entry.duration_seconds || 300) / 60"
                            (ngModelChange)="updateBreakDurationMinutes(entry, $event)"
                            [title]="'scheduleEditor.minutesUnit' | translate"
                          />
                          <span class="break-duration-unit">{{ 'scheduleEditor.minutesUnit' | translate }}</span>
                          <button class="remove-break" (click)="removeStartListBreak(entry)" title="Remove break">
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  } @else {
                    <!-- Participant Entry -->
                    <div
                      class="start-list-entry"
                      [class.dragging]="draggedStartListEntry?.entry_id === entry.entry_id"
                      [class.drag-over]="startListDragOverIndex === i"
                      draggable="true"
                      (dragstart)="onStartListDragStart($event, entry, i)"
                      (dragover)="onStartListDragOver($event, i)"
                      (drop)="onStartListDrop($event, i)"
                      (dragend)="onStartListDragEnd()"
                    >
                      <div class="entry-drag-handle">
                        <i class="fa fa-grip-vertical"></i>
                      </div>
                      <div class="entry-position">{{ entry.position }}</div>
                      <div class="entry-time">{{ getEntryEstimatedTime(entry, i) }}</div>
                      <div class="entry-info">
                        <span class="name">{{ getParticipantName(entry) }}</span>
                        @if (getHorseName(entry)) {
                          <span class="horse">{{ getHorseName(entry) }}</span>
                        }
                      </div>
                      <div class="entry-actions">
                        <button class="action-btn" data-bs-toggle="dropdown" [title]="'common.actions' | translate">
                          <i class="fa fa-ellipsis-h"></i>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end">
                          <li class="dropdown-header">{{ 'scheduleEditor.addBreakAfter' | translate }}</li>
                          <li>
                            <button class="dropdown-item" (click)="addStartListBreak(i, 120, 'Course fix')">
                              <i class="fa fa-wrench"></i> {{ 'scheduleEditor.courseFix2min' | translate }}
                            </button>
                          </li>
                          <li>
                            <button class="dropdown-item" (click)="addStartListBreak(i, 300, 'Course fix')">
                              <i class="fa fa-wrench"></i> {{ 'scheduleEditor.courseFix5min' | translate }}
                            </button>
                          </li>
                          <li>
                            <button class="dropdown-item" (click)="addStartListBreak(i, 600, 'Break')">
                              <i class="fa fa-coffee"></i> {{ 'scheduleEditor.break10min' | translate }}
                            </button>
                          </li>
                          <li>
                            <button class="dropdown-item" (click)="addStartListBreak(i, 900, 'Break')">
                              <i class="fa fa-coffee"></i> {{ 'scheduleEditor.break15min' | translate }}
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  }
                }
              </div>
            }
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Unsaved Changes Warning -->
  @if (showUnsavedWarning) {
    <div class="unsaved-overlay" (click)="cancelClose()">
      <div class="unsaved-dialog" (click)="$event.stopPropagation()">
        <div class="unsaved-icon">
          <i class="fa fa-exclamation-triangle"></i>
        </div>
        <h5>{{ 'scheduleEditor.unsavedChanges' | translate }}</h5>
        <p>{{ 'scheduleEditor.unsavedChangesMessage' | translate }}</p>
        <div class="unsaved-actions">
          <button class="btn-modern outline" (click)="discardAndClose()">
            {{ 'scheduleEditor.discardChanges' | translate }}
          </button>
          <button class="btn-modern primary" (click)="saveAndClose()">
            <i class="fa fa-save"></i>
            {{ 'scheduleEditor.saveAndClose' | translate }}
          </button>
        </div>
      </div>
    </div>
  }
</div>
